import { createFileRoute } from "@tanstack/react-router";
import { HandsFreePage } from "@/components/landing/MissionReadyPublicSite";
import { socialMeta } from "@/lib/social-meta";
import { ORG_WEBSITE_JSONLD, softwareApplicationJsonLd, SITE_URL } from "@/lib/structured-data";

const TITLE = "MissionReady Hands-Free Voice — Say “Hey MR” | MissionReady";
const DESCRIPTION =
  "MissionReady Hands-Free Voice lets authorized security professionals run supported MissionReady workflows by voice. Say “Hey MR”, speak the request, and get a spoken and on-screen answer inside your own role and site permissions.";

export const Route = createFileRoute("/hands-free")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      {
        name: "keywords",
        content:
          "hands-free voice security software, voice assistant for security guards, Hey MR, MissionReady Voice, voice-controlled security operations",
      },
      ...socialMeta({
        title: "MissionReady Hands-Free Voice — without the phone in your hand",
        description:
          "Say “Hey MR”, speak your request, and MissionReady responds out loud and on screen — inside your existing role and permissions.",
        url: `${SITE_URL}/hands-free`,
      }),
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: `${SITE_URL}/hands-free` }],
    scripts: [
      ...ORG_WEBSITE_JSONLD,
      softwareApplicationJsonLd({
        name: "MissionReady Hands-Free Voice",
        description: DESCRIPTION,
        url: `${SITE_URL}/hands-free`,
        featureList: [
          "“Hey MR” wake phrase where the device browser supports it",
          "Spoken request capture with Deepgram transcription",
          "Recognized request displayed on screen",
          "Role-aware answers limited by existing RBAC and site assignments",
          "Spoken and on-screen responses",
          "Confirmed handoff into existing MissionReady workflows",
          "Optional — enabled or disabled in Settings",
        ],
      }),
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
            { "@type": "ListItem", position: 2, name: "Hands-Free Voice", item: `${SITE_URL}/hands-free` },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "HowTo",
          name: "How to use MissionReady Hands-Free Voice",
          step: [
            { "@type": "HowToStep", name: "Enable it", text: "Turn on Hands-Free Voice in Settings → MissionReady Voice." },
            { "@type": "HowToStep", name: "Say “Hey MR”", text: "MissionReady shows a clear listening state, or tap the voice button." },
            { "@type": "HowToStep", name: "Speak the request", text: "The recognized request is transcribed and shown on screen." },
            { "@type": "HowToStep", name: "Get the answer", text: "MissionReady responds out loud and in text, limited to what your role allows." },
          ],
        }),
      },
    ],
  }),
  component: HandsFreePage,
});
